import React, { useState, useEffect, useRef } from 'react';
import './SortDropdown.css';

/**
 * Reusable Sort Dropdown Component
 * 
 * @param {Array} options - Array of sort options [{ value: 'key', label: 'Display Name' }]
 * @param {string} value - Current selected value
 * @param {Function} onChange - Callback when selection changes
 * @param {boolean} disabled - Whether the dropdown is disabled
 * @param {string} className - Additional CSS class name
 * @param {Object} chevronIcon - Icon configuration { src: string, alt: string, width: number, height: number }
 */
function SortDropdown({ 
  options = [], 
  value, 
  onChange, 
  disabled = false,
  className = '',
  chevronIcon = null
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const current = options.find(o => o.value === value) || options[0];

  const handleToggle = () => {
    if (!disabled) {
      setOpen(!open);
    }
  };

  const handleOptionClick = (e, optionValue) => {
    e.stopPropagation();
    onChange(optionValue);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !disabled) {
      setOpen(!open);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  // Default chevron icon (SVG)
  const defaultChevron = (
    <svg 
      width="16" 
      height="16" 
      viewBox="0 0 16 16" 
      fill="none"
      className="sort-dropdown__chevron"
    >
      <path 
        d="M4 6l4 4 4-4" 
        stroke="currentColor" 
        strokeWidth="1.5" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
    </svg>
  );

  return (
    <div
      ref={ref}
      className={`sort-dropdown ${open ? 'sort-dropdown--open' : ''} ${disabled ? 'sort-dropdown--disabled' : ''} ${className}`}
      onClick={handleToggle}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={handleKeyDown}
      aria-label="Sort options"
      aria-expanded={open}
      aria-disabled={disabled}
    >
      <span className="sort-dropdown__label">
        <span className="sort-dropdown__label-muted">Sort: </span>
        <span className="sort-dropdown__label-bold">{current?.label || 'Select'}</span>
      </span>
      
      {chevronIcon ? (
        <img
          src={chevronIcon.src}
          alt={chevronIcon.alt || ''}
          width={chevronIcon.width || 16}
          height={chevronIcon.height || 16}
          className="sort-dropdown__chevron"
        />
      ) : (
        defaultChevron
      )}
      
      {open && !disabled && (
        <div className="sort-dropdown__menu">
          {options.map(opt => (
            <button
              key={opt.value}
              type="button"
              className={`sort-dropdown__option ${opt.value === value ? 'sort-dropdown__option--active' : ''}`}
              onClick={(e) => handleOptionClick(e, opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default SortDropdown;
